import {
  Button,
  TextField,
  DatePicker,
  AdapterLuxon,
  Typography,
  Box,
  BottomNavigation,
  SearchOptionCard,
  Alert,
  LinearProgress,
  Tag,
  Tooltip,
} from '@octanner/prism-core'
import ExistingCelebrationInfo from './ExistingCelebrationInfo'
import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import useCreateNewCelebration from '../hooks/useCreateNewCelebrations'
import { DateTime } from 'luxon'
import { ResultModal } from './ResultModal'
import { FormFieldHelper } from './FormFieldHelper'
import { IdentityBase } from '../models/CelebrationGroupEdit'
import SingleSearchIdentitySelector, {
  formatIdentityName,
} from '../common/components/SingleSearchIdentitySelector'
import { tannerGray } from '@octanner/prism-core/ThemeProvider/colors'
import CelebrationLevelSelector from './CelebrationLevelSelector'
import { useGetExistingCelebrationDataLazy } from '../hooks/useGetExistingCelebrationDataLazy'
import { useGetExistingCelebrationData } from '../hooks/useGetExistingCelebrationData'
import { LaunchDarklyFlag } from './MainSettings'
import { Add, InformationCircle } from '@octanner/prism-icons'

export type NewCelebrationFormData = {
  identity: IdentityBase
  celebrationDate: DateTime
  celebrationLevel: number | null
}
export type NewCelebrationFormProps = {
  programId: string
  customerUuid: string
  onReroute: () => void
  launchDarklyFlags?: LaunchDarklyFlag[]
}

const NewCelebrationForm: React.FC<NewCelebrationFormProps> = ({
  programId,
  customerUuid,
  onReroute,
  launchDarklyFlags,
}) => {
  const { control, trigger, clearErrors, handleSubmit, watch, formState } =
    useForm<NewCelebrationFormData>({
      defaultValues: { celebrationDate: null },
    })
  const selectedIdentity = watch('identity')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [showCreateForm, setShowCreateForm] = useState(false)

  const manageCelebrationLDFlag =
    launchDarklyFlags?.find(
      (flag) => flag.key === 'ccAdminCcybYearbookManageCelebration20250319'
    )?.value ?? false

  useEffect(() => {
    setShowCreateForm(false)
  }, [selectedIdentity])

  const {
    data: existingCelebrationResponse,
    loading: isLoadingExistingCelebrationData,
  } = useGetExistingCelebrationData(selectedIdentity?.identityUuid)

  const { getExistingCelebrations } = useGetExistingCelebrationDataLazy()

  const existingCelebration =
    existingCelebrationResponse?.ybCelebrationByIdentity

  const existingCelebrationId = existingCelebration?.id

  const hasPrivateCelebration =
    existingCelebration?.identity.yearbookPreferences?.hasPrivateCelebrations ??
    false

  const {
    createNewCelebration,
    loading: loadingCreateNewCelebratoin,
    allErrors: createCelebrationError,
  } = useCreateNewCelebration()

  const onSubmit = (data: NewCelebrationFormData) => {
    createNewCelebration({
      input: [
        {
          identityUuid: data.identity.identityUuid,
          celebrationDate: data.celebrationDate
            .startOf('day')
            .toFormat('yyyy-LL-dd'),
          celebrationLevel: data.celebrationLevel,
          programUuid: programId,
        },
      ],
      onSettled: () => {
        setIsModalOpen(true)
      },
    })
  }

  const hasExisingCelebration = existingCelebration && existingCelebrationId

  const renderBottomActions = () => {
    if (hasExisingCelebration) {
      return (
        <Button
          onClick={onReroute}
          color="secondary"
          data-testid="back-to-program-button"
        >
          Back to program
        </Button>
      )
    }
    if (showCreateForm || (selectedIdentity && !manageCelebrationLDFlag)) {
      return (
        <>
          <Button loading={loadingCreateNewCelebratoin} type="submit">
            Create celebration
          </Button>
          <Button
            color="secondary"
            disabled={loadingCreateNewCelebratoin}
            data-testid="cancel-button"
            onClick={() => onReroute()}
          >
            Cancel
          </Button>{' '}
        </>
      )
    }
    return (
      <Button onClick={onReroute} color="secondary" data-testid="cancel-button">
        Cancel
      </Button>
    )
  }

  const renderFormContent = () => {
    if (!selectedIdentity) {
      return null
    }
    if (isLoadingExistingCelebrationData) {
      return (
        <Box
          data-testid="loading-existing-celebration-data"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          <Box
            sx={{
              width: '240px',
              height: '24px',
              borderRadius: '3px',
              backgroundColor: tannerGray[50],
            }}
          ></Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: '467px',
                height: '47px',
                borderRadius: '3px',
                backgroundColor: tannerGray[50],
              }}
            ></Box>
            <Box
              sx={{
                width: '467px',
                height: '47px',
                borderRadius: '3px',
                backgroundColor: tannerGray[50],
              }}
            ></Box>
            <Box
              sx={{
                width: '467px',
                height: '47px',
                borderRadius: '3px',
                backgroundColor: tannerGray[50],
              }}
            ></Box>
          </Box>
        </Box>
      )
    }
    if (hasExisingCelebration) {
      return manageCelebrationLDFlag ? (
        <ExistingCelebrationInfo celebration={existingCelebration} />
      ) : null
    }
    if (!manageCelebrationLDFlag || showCreateForm) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            visibility:
              selectedIdentity &&
              !isLoadingExistingCelebrationData &&
              !formState.errors.identity
                ? 'unset'
                : 'hidden',
          }}
        >
          <FormFieldHelper
            title="Enter celebration details"
            subtitle="Choose the anniversary year and celebration date for which you want to create this celebration"
          />
          <Controller
            name="celebrationLevel"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <CelebrationLevelSelector
                programId={programId}
                onChange={field.onChange}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                identityUuid={selectedIdentity?.identityUuid}
                {...field}
              />
            )}
          />
          <Controller
            name="celebrationDate"
            control={control}
            rules={{
              required: 'Required',
              validate: (date: DateTime) => {
                if (!date.isValid) {
                  return 'Enter date in DD MMM YYYY format'
                }
                return true
              },
            }}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                label="Celebration date"
                dateAdapter={AdapterLuxon}
                disableMaskedInput
                disablePast
                disableFuture={false}
                minDate={DateTime.now().startOf('day').plus({ days: 1 })}
                maxDate={DateTime.now().startOf('day').plus({ years: 1 })}
                renderInput={(params) => (
                  <TextField
                    sx={{ width: '400px' }}
                    {...params}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </Box>
      )
    }
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '32px',
          gap: '32px',
          alignItems: 'center',
          backgroundColor: tannerGray[50],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography fontWeight={800} fontSize={16}>
            No active celebrations
          </Typography>
          <Typography
            variant="body1"
            color={tannerGray[700]}
            sx={{ textAlign: 'center' }}
          >
            This employee has no active celebrations at this time. Once their
            celebration window is active, their celebration details will be
            viewable here.{' '}
          </Typography>
        </Box>
        <Button
          onClick={() => {
            setShowCreateForm(true)
          }}
          startIcon={<Add />}
        >
          Add new celebration
        </Button>
      </Box>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '949px',
            marginLeft: '32px',
            gap: '24px',
          }}
        >
          <Box>
            <Typography variant="h1">
              {manageCelebrationLDFlag
                ? 'Manage Celebrations'
                : 'Add New Celebration'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <FormFieldHelper
                title="Select employee"
                subtitle={
                  manageCelebrationLDFlag
                    ? 'Search for an employee in order to view or create a celebration.'
                    : 'Choose the employee you want to create a celebration for'
                }
              />
              <Controller
                name="identity"
                control={control}
                rules={{
                  required:
                    'You must have an employee selected in order to create this celebration.',
                  validate: async (selectedIdentity) => {
                    try {
                      await getExistingCelebrations({
                        variables: {
                          input: {
                            identityUuid: selectedIdentity?.identityUuid,
                          },
                        },
                      })
                      return true
                    } catch (e) {
                      return e.message
                    }
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                      }}
                    >
                      <SingleSearchIdentitySelector
                        {...field}
                        onChange={(value) => {
                          field.onChange(value)
                          if (value) {
                            trigger('identity')
                          } else {
                            clearErrors('identity')
                          }
                        }}
                        data-testid="single-search-identity-selector"
                        customerId={customerUuid}
                        identity={field.value}
                        placeholder="Search for employee"
                        renderOption={(props, option) => (
                          <SearchOptionCard
                            {...props}
                            data-testid={
                              'identity-selector:option:' + option.id
                            }
                            // @ts-ignore incorrect type in prism
                            title={
                              <span>
                                {formatIdentityName(option as IdentityBase)}
                                <Typography
                                  variant="body2"
                                  color={tannerGray[600]}
                                  component={'span'}
                                >{` • Employee ID: ${option.uniqueId}`}</Typography>
                              </span>
                            }
                            subTitle={`${option.email}  • UUID: ${option.identityUuid}`}
                            avatarUrl={option.profile?.avatar}
                            showAvatar
                          />
                        )}
                      />
                      {isLoadingExistingCelebrationData && <LinearProgress />}
                      {(existingCelebrationId || fieldState.error) &&
                        !manageCelebrationLDFlag && (
                          <Alert severity="error">
                            {fieldState.error?.message ??
                              'There is already an active celebration for this employee. Please select another employee.' ??
                              ''}
                          </Alert>
                        )}
                    </Box>
                  )
                }}
              />
            </Box>
            {selectedIdentity && manageCelebrationLDFlag && (
              <Box>
                <Typography variant="h2">
                  {selectedIdentity.firstName} {selectedIdentity.lastName}{' '}
                  {hasPrivateCelebration && (
                    <Tag variant="archived" color={tannerGray[100]}>
                      {' '}
                      Privacy
                      <Tooltip
                        title="This employee has settings turned to private, causing any actions in the timeline that are marked with the privacy tag to be omitted from the celebration."
                        placement="top"
                        style={{ marginLeft: 8 }}
                      >
                        <InformationCircle sx={{ color: tannerGray[400] }} />
                      </Tooltip>
                    </Tag>
                  )}
                </Typography>

                <Typography variant="body1">
                  Employee ID: {selectedIdentity.uniqueId} | UUID:{' '}
                  {selectedIdentity.identityUuid} | {selectedIdentity.email}
                </Typography>
              </Box>
            )}
            {renderFormContent()}
          </Box>
        </Box>

        <BottomNavigation
          sx={{
            position: 'fixed',
            bottom: 0,
            py: '16px',
            px: '40px',
            boxShadow: '0px 4px 14px 0px #00000033',
            justifyContent: 'space-between',
            width: '100%',
            height: '72px',
            marginLeft: '-8px',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 5 }}>{renderBottomActions()}</Box>
        </BottomNavigation>
      </form>
      <ResultModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
        }}
        handleBackToProgram={() => {
          setIsModalOpen(false)
          onReroute()
        }}
        manageCelebrationLDFlag={manageCelebrationLDFlag}
        handleGoToSchedule={async () => {
          try {
            await getExistingCelebrations({
              variables: {
                input: { identityUuid: selectedIdentity?.identityUuid },
              },
              fetchPolicy: 'network-only',
            })
          } catch (e) {
            console.log('failed to fetch celebration data', e)
          }
          setIsModalOpen(false)
        }}
        error={createCelebrationError}
        successMessage={
          manageCelebrationLDFlag
            ? `The celebration created for ${selectedIdentity?.firstName} ${selectedIdentity?.lastName} was successful and the celebration actions will begin. View the schedule to see when the actions will be occurring.`
            : `The celebration created for ${selectedIdentity?.firstName} ${selectedIdentity?.lastName} was successful.`
        }
      />
    </>
  )
}

export default NewCelebrationForm
